import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  getPortalAccount,
  listPortalAgreements,
  listPortalAppointments,
  listPortalDocuments,
  listPortalOpportunities,
  type PortalAccount,
  type PortalAgreement,
  type PortalAppointment,
  type PortalDocument,
  type PortalOpportunity,
} from '@/api/portal';

/**
 * Read-only client dashboard (story 128.3): proof-of-performance scoped to the
 * client's own account — pipeline/deal status, upcoming appointments, shared
 * documents, and agreements. Pure reads; no mutating actions are exposed.
 */
export function PortalDashboardPage() {
  const { t, i18n } = useTranslation();
  const [account, setAccount] = useState<PortalAccount | null>(null);
  const [opps, setOpps] = useState<PortalOpportunity[]>([]);
  const [appointments, setAppointments] = useState<PortalAppointment[]>([]);
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [agreements, setAgreements] = useState<PortalAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getPortalAccount(),
      listPortalOpportunities(),
      listPortalAppointments(),
      listPortalDocuments(),
      listPortalAgreements(),
    ])
      .then(([acc, o, ap, d, ag]) => {
        if (cancelled) return;
        setAccount(acc);
        setOpps(o);
        setAppointments(ap);
        setDocuments(d);
        setAgreements(ag);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fmtDate = useMemo(
    () => (iso: string | null) =>
      iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—',
    [i18n.language],
  );
  const fmtMoney = useMemo(
    () => (value: number | null, currency: string | null) =>
      value == null
        ? '—'
        : new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 0,
          }).format(value),
    [i18n.language],
  );

  const openOpps = opps.filter((o) => o.status === 'OPEN');
  const pipelineValue = openOpps.reduce((sum, o) => sum + (o.estimatedDealValue ?? 0), 0);
  const upcomingAppointments = appointments.filter((a) => a.status !== 'COMPLETED' && a.status !== 'CANCELLED');
  const signedAgreements = agreements.filter((a) => a.signedDate != null);
  const pipelineCurrency = openOpps.find((o) => o.currency)?.currency ?? 'USD';

  if (loading) {
    return <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('portal.common.loading')}</p>;
  }
  if (error) {
    return <p className="text-sm text-[var(--color-lop-danger)]">{t('portal.common.error')}</p>;
  }

  return (
    <div className="space-y-8">
      {/* Account header */}
      <div>
        <h1 className="text-2xl font-semibold">{account?.name ?? t('portal.dashboard.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
          {[account?.industry, [account?.city, account?.region, account?.country].filter(Boolean).join(', ')]
            .filter(Boolean)
            .join(' · ') || t('portal.dashboard.subtitle')}
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label={t('portal.kpi.openDeals')} value={String(openOpps.length)} />
        <Kpi label={t('portal.kpi.pipelineValue')} value={fmtMoney(pipelineValue, pipelineCurrency)} />
        <Kpi label={t('portal.kpi.upcomingAppointments')} value={String(upcomingAppointments.length)} />
        <Kpi label={t('portal.kpi.signedAgreements')} value={String(signedAgreements.length)} />
      </div>

      {/* Pipeline / opportunities */}
      <Card>
        <CardHeader>
          <CardTitle>{t('portal.sections.opportunities')}</CardTitle>
        </CardHeader>
        {opps.length === 0 ? (
          <Empty text={t('portal.empty.opportunities')} />
        ) : (
          <Table
            head={[
              t('portal.cols.name'),
              t('portal.cols.status'),
              t('portal.cols.value'),
              t('portal.cols.progress'),
              t('portal.cols.closeDate'),
            ]}
            rows={opps.map((o) => [
              o.name,
              <StatusPill key="s" status={o.status} />,
              fmtMoney(o.estimatedDealValue, o.currency),
              o.progressPercent != null ? `${Math.round(o.progressPercent)}%` : '—',
              fmtDate(o.expectedCloseDate),
            ])}
          />
        )}
      </Card>

      {/* Upcoming appointments */}
      <Card>
        <CardHeader>
          <CardTitle>{t('portal.sections.appointments')}</CardTitle>
        </CardHeader>
        {upcomingAppointments.length === 0 ? (
          <Empty text={t('portal.empty.appointments')} />
        ) : (
          <Table
            head={[
              t('portal.cols.opportunity'),
              t('portal.cols.status'),
              t('portal.cols.when'),
              t('portal.cols.meeting'),
            ]}
            rows={upcomingAppointments.map((a) => [
              a.opportunityName,
              <StatusPill key="s" status={a.status} />,
              fmtDate(a.confirmedAt ?? a.bookedAt),
              a.meetingLink ? (
                <a
                  key="l"
                  href={a.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-lop-teal)] underline"
                >
                  {t('portal.cols.join')}
                </a>
              ) : (
                '—'
              ),
            ])}
          />
        )}
      </Card>

      {/* Shared documents */}
      <Card>
        <CardHeader>
          <CardTitle>{t('portal.sections.documents')}</CardTitle>
        </CardHeader>
        {documents.length === 0 ? (
          <Empty text={t('portal.empty.documents')} />
        ) : (
          <Table
            head={[
              t('portal.cols.title'),
              t('portal.cols.type'),
              t('portal.cols.status'),
              t('portal.cols.opportunity'),
              t('portal.cols.created'),
            ]}
            rows={documents.map((d) => [
              d.title,
              d.documentType,
              <StatusPill key="s" status={d.status} />,
              d.opportunityName,
              fmtDate(d.createdAt),
            ])}
          />
        )}
      </Card>

      {/* Agreements */}
      <Card>
        <CardHeader>
          <CardTitle>{t('portal.sections.agreements')}</CardTitle>
        </CardHeader>
        {agreements.length === 0 ? (
          <Empty text={t('portal.empty.agreements')} />
        ) : (
          <Table
            head={[
              t('portal.cols.title'),
              t('portal.cols.kind'),
              t('portal.cols.status'),
              t('portal.cols.effective'),
              t('portal.cols.signed'),
            ]}
            rows={agreements.map((a) => [
              a.title,
              a.kind,
              <StatusPill key="s" status={a.status} />,
              fmtDate(a.effectiveDate),
              fmtDate(a.signedDate),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--color-lop-text-muted)]">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </Card>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-[var(--color-lop-text-muted)]">{text}</p>;
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className="inline-block rounded-full border border-[var(--color-lop-border-bright)] bg-[var(--color-lop-bg-mid)] px-2 py-0.5 text-xs text-[var(--color-lop-text-secondary)]">
      {status}
    </span>
  );
}

function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-lop-border)]">
            {head.map((h) => (
              <th
                key={h}
                className="py-2 pr-4 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-lop-text-muted)]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-b border-[var(--color-lop-border)]/40">
              {cells.map((c, j) => (
                <td key={j} className="py-2.5 pr-4 align-top text-[var(--color-lop-text-primary)]">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
